import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, apiError, type ApiErrorShape } from '@/lib/api';
import { initTelegram } from '@/lib/telegram';
import { useI18n, type Lang } from '@/i18n';
import type { Me } from '@/types/api';

interface AuthValue {
    loading: boolean;
    me: Me | null;
    error: ApiErrorShape | null;
    refresh: () => Promise<Me | null>;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const { setLang } = useI18n();
    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState<Me | null>(null);
    const [error, setError] = useState<ApiErrorShape | null>(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get<Me>('/me');
            setMe(data);
            setError(null);
            if (data.user.lang === 'pt' || data.user.lang === 'en') setLang(data.user.lang as Lang);
            return data;
        } catch (e) {
            setError(apiError(e));
            return null;
        } finally {
            setLoading(false);
        }
    }, [setLang]);

    useEffect(() => {
        initTelegram();
        void refresh();
    }, [refresh]);

    return <Ctx.Provider value={{ loading, me, error, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
    const v = useContext(Ctx);
    if (!v) throw new Error('useAuth outside AuthProvider');
    return v;
}
